import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import config from "../../../config/config";

const stripePromise = loadStripe(
  "pk_test_51O1JdRK1XgZ7Sqhtsq8wZRGcpxoBDKRX4oz6Grx9OlZyLTbKk9Wcnv6UrcxKhiOntgsmHGWHBC2CvvrY6k5IEfMw00eHLphmKb"
);

const PaymentModal = ({
  show,
  handleClose,
  clientSecret,
  firstName,
  lastName,
  email,
  setCustomerId,
  user,
}) => {
  if (!show) return null;

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal-content">
        <button
          className="payment-modal-close-button"
          onClick={() => {
            handleClose();
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              clientSecret={clientSecret}
              handleClose={handleClose}
              firstName={firstName}
              lastName={lastName}
              email={email}
              setCustomerId={setCustomerId}
              user={user}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};

const PaymentForm = ({ clientSecret, handleClose, user }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const messageRef = useRef(null);

  useEffect(() => {
    let timer;
    if (isSubmitting) {
      timer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
    return () => clearInterval(timer);
  }, [isSubmitting]);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setIsSubmitting(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url:
          "http://localhost:3000/account/fs-dashboard/my-account/payment-details",
      },
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(error.message);
      messageRef.current?.scrollIntoView({ behavior: "smooth" });
      setIsSubmitting(false);
      setProgress(0);
      return;
    }

    if (setupIntent.status === "succeeded" && setupIntent.payment_method) {
      const paymentMethodId = setupIntent.payment_method;

      try {
        const { data: paymentMethodResponse } = await axios.get(
          `${config.API_BASE_URL}/v1/stripe/retrievePaymentMethod/${paymentMethodId}`
        );
        const paymentMethod = paymentMethodResponse.paymentMethod;
        const billingDetails = paymentMethod.billingDetails;

        console.log(
          "Setup Intent:",
          setupIntent,
          "Payment Method:",
          paymentMethod
        );

        const body = {
          setupIntentId: setupIntent.id,
        };

        try {
          const { data: finishSetup } = await axios.post(
            `${config.API_BASE_URL}/v1/stripe/finishsetup`,
            body
          );

          if (finishSetup.success) {
            setSuccessMessage("Payment setup successfully!");
            setErrorMessage("");
            messageRef.current?.scrollIntoView({ behavior: "smooth" });

            const updates = {
              paymentDetails: {
                usingGovernmentFunding: false,
                paymentIntentDetails: {
                  paymentMethodId: paymentMethodId,
                  customerId: finishSetup.customer,
                  paymentType: "card",
                  postalCode: billingDetails.address.postalCode,
                  country: billingDetails.address.country,
                },
              },
            };

            console.log("PaymentDetails Patch Updates:", updates);

            try {
              const response = await axios.patch(
                `${config.API_BASE_URL}/v1/client-users/${user.id}`,
                updates,
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );
              if (response.data) {
                console.log("Client user updated successfully:", response.data);
                handleClose();
              }
            } catch (patchError) {
              if (patchError.response) {
                console.error(
                  "Error updating client user:",
                  patchError.response.data
                );
              } else {
                console.error(
                  "Error updating client user:",
                  patchError.message
                );
              }
            }
          } else {
            setErrorMessage("Payment setup failed.");
            messageRef.current?.scrollIntoView({ behavior: "smooth" });
          }
        } catch (error) {
          setErrorMessage(error.message);
          messageRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      } catch (retrieveError) {
        setErrorMessage("Failed to retrieve payment method details.");
        messageRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      setErrorMessage(
        "Setup intent not confirmed or payment method not found."
      );
      messageRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="stripe-payment-form">
      <div className="stripe-partnership">
        <p>
          We partner with and use <strong>Stripe</strong> to save and process
          payments securely. By leveraging Stripe's secure infrastructure, we
          ensure that your payment information is protected at all times, giving
          you peace of mind with every transaction.
        </p>
      </div>
      <form onSubmit={handlePaymentSubmit}>
        <div className="payment-element-container">
          <PaymentElement
            options={{
              layout: "tabs",
              paymentMethodOrder: ["card", "us_bank_account"],
            }}
          />
        </div>
        <div className="stripe-badge">
          <div className="powered-by">
            <span>Powered By</span>
            <img src="/images/stripe/logo.png" alt="Powered by Stripe" />
          </div>
          <div className="stripe-links" style={{ marginTop: "5px" }}>
            <p>
              <a
                href="https://stripe.com/legal/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms
              </a>{" "}
              |{" "}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy
              </a>
            </p>
          </div>
        </div>
        {successMessage && (
          <div className="fs-payment-success-message" ref={messageRef}>
            <p>{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="fs-payment-error" ref={messageRef}>
            {errorMessage}
          </div>
        )}
        {isSubmitting && (
          <div className="fs-payment-progress-bar" ref={messageRef}>
            <div
              className="fs-payment-progress-bar-inner"
              style={{ width: `${progress}%` }}
            >
              {progress}%
            </div>
          </div>
        )}
        <div className="fs-payment-save" ref={messageRef}>
          <button type="submit" disabled={!stripe || isSubmitting}>
            Save to Stripe Wallet
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentModal;
